import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  BarChart3,
  FileText,
  Gauge,
  HeartHandshake,
  Inbox,
  Layers,
  BanknoteIcon,
  Library,
  PenLine,
  UserCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/_authenticated/studio")({
  component: StudioLayout,
});

const STUDIO_NAV = [
  { to: "/studio", label: "Overview", icon: Gauge, exact: true },
  { to: "/studio/publications", label: "Publications", icon: Library, exact: false },
  { to: "/studio/drafts", label: "Drafts", icon: FileText, exact: false },
  { to: "/studio/submissions", label: "Submissions", icon: Inbox, exact: false },
  { to: "/studio/series", label: "Series", icon: Layers, exact: false },
  { to: "/studio/analytics", label: "Analytics", icon: BarChart3, exact: false },
  { to: "/studio/support", label: "Support", icon: HeartHandshake, exact: false },
  { to: "/studio/payouts", label: "Payouts", icon: BanknoteIcon, exact: false },
  { to: "/studio/profile", label: "Writer profile", icon: UserCircle, exact: false },
] as const;

function StudioLayout() {
  const { loading, isWriter } = useAuth();

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-12 sm:px-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isWriter) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center sm:px-6">
        <h1 className="font-display text-3xl text-foreground">Creator Studio is for Writers</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Writer Mode isn't active on your account yet. Activating it adds publishing tools to this
          same account — you keep your profile, followers, library and history.
        </p>
        <Button asChild variant="hero" size="pill" className="mt-6">
          <Link to="/start-writing">Start writing</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t border-border/60 bg-surface/30">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Creator Studio
            </p>
            <h1 className="mt-1 font-display text-3xl tracking-tight text-foreground">
              Your writing workspace
            </h1>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="soft" size="pill">
              <Link to="/">Reader home</Link>
            </Button>
            <Button asChild variant="hero" size="pill">
              <Link to="/write">
                <PenLine className="h-4 w-4" aria-hidden="true" />
                Create publication
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[220px_1fr]">
          <nav
            aria-label="Creator Studio"
            className="-mx-1 flex gap-1 overflow-x-auto pb-2 lg:mx-0 lg:flex-col lg:overflow-visible lg:pb-0"
          >
            {STUDIO_NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.exact }}
                activeProps={{ className: "bg-accent text-foreground" }}
                className="flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground lg:rounded-2xl"
              >
                <item.icon className="h-4 w-4" aria-hidden="true" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
